import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { fetchCostAudit } from '../../lib/api';

const FinancialAudit = () => {
  const [data, setData] = useState(null);
  const [openSlug, setOpenSlug] = useState(null);

  useEffect(() => {
    fetchCostAudit().then(setData).catch(() => {});
  }, []);

  if (!data)
    return <div data-testid="audit-loading" className="h-40 bg-[#C1D7E2]/40 animate-pulse" />;

  return (
    <div data-testid="audit-view" className="space-y-8">
      <header className="border-b border-[#C1D7E2] pb-6">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-2">
          Artha Integrity · Cost-Plus-25 Proof
        </p>
        <h3 className="font-serif text-3xl md:text-4xl text-[#7C9CB1] font-light">
          Every transaction, audited.
        </h3>
        <p className="mt-2 font-serif italic text-[#90B0BF]">{data.policy}</p>
        <p className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[#7C9CB1]">
          Logistics pass-through at 0 % markup — we do not profit from third-party shipping.
        </p>
      </header>

      <div data-testid="audit-table" className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-[#C1D7E2]">
              <th className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#90B0BF] py-4 pr-4 font-medium w-6"></th>
              {[
                'SKU',
                'Source',
                'Retail',
                'Cost',
                'Margin',
                'Logistics (0%)',
                'Production',
                'Glass & Labour',
              ].map((h) => (
                <th
                  key={h}
                  className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#90B0BF] py-4 pr-6 font-medium"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.products.map((p, i) => {
              const expandable = p.source === 'formula' && Array.isArray(p.ingredients);
              const isOpen = openSlug === p.slug;
              return (
                <React.Fragment key={p.slug}>
                  <tr
                    data-testid={`audit-row-${i}`}
                    className={`border-b border-[#C1D7E2]/60 hover:bg-[#C1D7E2]/15 ${
                      expandable ? 'cursor-pointer' : ''
                    }`}
                    onClick={() =>
                      expandable && setOpenSlug(isOpen ? null : p.slug)
                    }
                  >
                    <td className="py-4 pr-4 align-top">
                      {expandable ? (
                        isOpen ? (
                          <ChevronDown size={14} className="text-[#7C9CB1]" />
                        ) : (
                          <ChevronRight size={14} className="text-[#7C9CB1]" />
                        )
                      ) : null}
                    </td>
                    <td className="py-4 pr-6">
                      <p className="font-serif text-base text-[#7C9CB1]">{p.name}</p>
                      <p className="font-mono text-[10px] text-[#90B0BF]">{p.slug}</p>
                    </td>
                    <td className="py-4 pr-6">
                      <span
                        data-testid={`audit-source-${i}`}
                        className={`font-mono text-[10px] tracking-[0.18em] uppercase px-2 py-1 border ${
                          p.source === 'formula'
                            ? 'border-[#7C9CB1] text-[#7C9CB1] bg-[#C1D7E2]/30'
                            : 'border-[#C1D7E2] text-[#90B0BF]'
                        }`}
                      >
                        {p.source}
                      </span>
                    </td>
                    <td className="py-4 pr-6 font-mono text-sm text-[#7C9CB1]">
                      ${p.retail_usd.toFixed(2)}
                    </td>
                    <td className="py-4 pr-6 font-mono text-sm text-[#7C9CB1]">
                      ${p.cost_usd.toFixed(2)}
                    </td>
                    <td className="py-4 pr-6 font-mono text-sm text-[#7C9CB1]">
                      ${p.margin_usd.toFixed(2)}
                    </td>
                    <td className="py-4 pr-6 font-mono text-sm text-[#7C9CB1]">
                      ${p.logistics_usd_pass_through.toFixed(2)}
                    </td>
                    <td className="py-4 pr-6 font-mono text-sm text-[#7C9CB1]">
                      ${p.production_usd.toFixed(2)}
                    </td>
                    <td className="py-4 pr-6 font-mono text-sm text-[#7C9CB1]">
                      ${p.glass_and_labour_usd.toFixed(2)}
                    </td>
                  </tr>
                  {expandable && isOpen && (
                    <tr
                      data-testid={`audit-detail-${p.slug}`}
                      className="bg-[#F4F8F9]"
                    >
                      <td colSpan={9} className="px-6 py-6">
                        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-4">
                          Per-ingredient raw cost · {p.volume_ml} ml batch
                        </p>
                        <div className="grid grid-cols-12 gap-3 font-mono text-[10px] tracking-[0.2em] uppercase text-[#90B0BF] border-b border-[#C1D7E2]/60 pb-2 mb-2">
                          <span className="col-span-5">Ingredient</span>
                          <span className="col-span-2 text-right">% of final</span>
                          <span className="col-span-2 text-right">Mass (g)</span>
                          <span className="col-span-2 text-right">$/kg</span>
                          <span className="col-span-1 text-right">Cost</span>
                        </div>
                        {p.ingredients.map((ing, j) => (
                          <div
                            key={ing.name + j}
                            data-testid={`audit-ing-${p.slug}-${j}`}
                            className="grid grid-cols-12 gap-3 py-2 border-b border-[#C1D7E2]/30 text-[#7C9CB1]"
                          >
                            <span className="col-span-5">
                              <span className="font-serif text-sm">{ing.name}</span>
                              <span className="block text-[10px] italic text-[#90B0BF]">
                                {ing.role}
                              </span>
                            </span>
                            <span className="col-span-2 text-right font-mono text-xs">
                              {ing.percent_of_final}%
                            </span>
                            <span className="col-span-2 text-right font-mono text-xs">
                              {ing.mass_g.toFixed(2)}
                            </span>
                            <span className="col-span-2 text-right font-mono text-xs">
                              ${ing.cost_per_kg_usd.toFixed(2)}
                            </span>
                            <span className="col-span-1 text-right font-mono text-xs">
                              ${ing.cost_usd.toFixed(3)}
                            </span>
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FinancialAudit;
